import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabaseClient';
import { API_ENDPOINTS, BACKEND_NODE_URL } from '../config/constants';
import './GasDistributorClientModal.css';
import GasDistributorPedidoModal from './GasDistributorPedidoModal';
import GasDistributorNovoPedidoModal from './GasDistributorNovoPedidoModal';
import CRMConversationModal from './CRMConversationModal';
import AppIcon from './icons/AppIcon';

export default function GasDistributorClientModal({ card, projectId: projectIdProp, companyId, onClose }) {
  void companyId;
  const [cliente, setCliente] = useState(null);
  const [contato, setContato] = useState(null);
  const [enderecos, setEnderecos] = useState([]);
  const [pedidos, setPedidos] = useState([]);
  const [ultimoPedido, setUltimoPedido] = useState(null);
  const [conversas, setConversas] = useState([]);
  const [messages, setMessages] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('dados'); // 'dados', 'enderecos', 'pedidos', 'conversas', 'tags'
  const [avatarError, setAvatarError] = useState(false);
  const [processedAvatarUrl, setProcessedAvatarUrl] = useState(null);
  const [tagsDisponiveis, setTagsDisponiveis] = useState([]);
  const [tagsCliente, setTagsCliente] = useState([]);
  const [loadingTags, setLoadingTags] = useState(false);
  const [tagsPendentes, setTagsPendentes] = useState([]); // Tags adicionadas mas não salvas
  const [tagsRemovidas, setTagsRemovidas] = useState([]); // Tags removidas mas não salvas
  const [selectedPedidoId, setSelectedPedidoId] = useState(null); // Pedido selecionado para visualizar
  const [showNovoPedido, setShowNovoPedido] = useState(false);
  const [showConversationModal, setShowConversationModal] = useState(false);

  const [showEnderecoForm, setShowEnderecoForm] = useState(false);
  const [savingEndereco, setSavingEndereco] = useState(false);
  const [editingEnderecoId, setEditingEnderecoId] = useState(null);
  const [novoEndereco, setNovoEndereco] = useState({
    apelido: '',
    endereco: '',
    numero: '',
    complemento: '',
    bairro: '',
    cidade: '',
    cep: ''
  });

  const effectiveProjectId = projectIdProp || card?.project_id || null;
  const resolvedConnectionId =
    card?.metadata?.connection_id ||
    selectedConversation?.conexao_id ||
    null;

  useEffect(() => {
    if (card.entity_type === 'gas_distributor_client' && card.entity_id) {
      loadClientData();
    }
  }, [card.entity_id]);

  const loadClientData = async () => {
    try {
      setLoading(true);

      // Buscar dados do cliente distribuidor
      const { data: clienteData, error: clienteError } = await supabase
        .from('cliente_distribuidor')
        .select('*')
        .eq('id', card.entity_id)
        .single();

      if (clienteError) throw clienteError;
      setCliente(clienteData);
      
      // Inicializar tags do cliente
      setTagsCliente(clienteData?.tags_desconto || []);

      // Buscar dados do contato
      if (clienteData?.contato_id) {
        const { data: contatoData } = await supabase
          .from('contatos')
          .select('*')
          .eq('id', clienteData.contato_id)
          .single();
        setContato(contatoData);

        // Buscar conversas do contato
        const { data: conversasData } = await supabase
          .from('conversas')
          .select('*')
          .eq('contato_id', clienteData.contato_id)
          .order('atualizado_em', { ascending: false });
        setConversas(conversasData || []);

        if (conversasData && conversasData.length > 0) {
          setSelectedConversation(conversasData[0]);
          loadMessages(conversasData[0].id);
        }
      }

      // Buscar endereços
      const { data: enderecosData, error: enderecosError } = await supabase
        .from('cliente_endereco')
        .select('*')
        .eq('cliente_distribuidor_id', card.entity_id)
        .order('eh_principal', { ascending: false });
      
      if (enderecosError) {
        console.error('Erro ao buscar endereços:', enderecosError);
      }
      console.log('Endereços carregados:', enderecosData);
      setEnderecos(enderecosData || []);

      // Buscar pedidos
      const { data: pedidosData } = await supabase
        .from('pedido_gas_distributor')
        .select('*')
        .eq('cliente_id', card.entity_id)
        .order('created_at', { ascending: false });
      setPedidos(pedidosData || []);
      
      // Definir último pedido
      if (pedidosData && pedidosData.length > 0) {
        setUltimoPedido(pedidosData[0]);
      }

      // Buscar tags disponíveis do projeto para exibir nomes corretamente
      const { data: projectData } = await supabase
        .from('project_items')
        .select('project_id')
        .eq('id', card.id)
        .single();
      
      if (projectData?.project_id) {
        const { data: projectSettings } = await supabase
          .from('projects')
          .select('settings')
          .eq('id', projectData.project_id)
          .single();
        
        if (projectSettings?.settings?.gas_distributor?.tags_configuradas) {
          setTagsDisponiveis(projectSettings.settings.gas_distributor.tags_configuradas);
        }
      }

    } catch (error) {
      console.error('Erro ao carregar dados do cliente:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadMessages = async (conversationId) => {
    try {
      const { data: messagesData, error } = await supabase
        .from('mensagens')
        .select('id, conteudo, tipo_conteudo, direcao, from_me, criado_em, url_midia, transcricao')
        .eq('conversa_id', conversationId)
        .order('criado_em', { ascending: false })
        .limit(50);

      if (error) throw error;
      setMessages((messagesData || []).reverse());
    } catch (error) {
      console.error('Erro ao carregar mensagens:', error);
    }
  };

  const handleSalvarNovoEndereco = async (e) => {
    e.preventDefault();
    if (!novoEndereco.endereco?.trim()) {
      alert('Preencha o logradouro (endereço).');
      return;
    }
    setSavingEndereco(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      if (!token) {
        alert('Sessão expirada. Faça login novamente.');
        return;
      }

      const isEditing = !!editingEnderecoId;
      const endpoint = isEditing
        ? `${BACKEND_NODE_URL}/api/gas-distributor/enderecos/${encodeURIComponent(editingEnderecoId)}`
        : `${BACKEND_NODE_URL}/api/gas-distributor/clients/${encodeURIComponent(card.entity_id)}/enderecos`;
      const method = isEditing ? 'PUT' : 'POST';

      const res = await fetch(endpoint, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          apelido: novoEndereco.apelido?.trim() || null,
          endereco: novoEndereco.endereco.trim(),
          numero: novoEndereco.numero?.trim() || '',
          complemento: novoEndereco.complemento?.trim() || '',
          bairro: novoEndereco.bairro?.trim() || '',
          cidade: novoEndereco.cidade?.trim() || '',
          cep: novoEndereco.cep?.trim() || ''
        })
      });

      const json = await res.json();
      if (!res.ok) {
        alert(json.error || 'Erro ao salvar endereço');
        return;
      }

      handleCancelarEdicaoEndereco();
      await loadClientData();
    } catch (err) {
      console.error(err);
      alert(err.message || 'Erro ao salvar endereço');
    } finally {
      setSavingEndereco(false);
    }
  };

  const handleIniciarEdicaoEndereco = (endereco) => {
    if (!endereco?.id) return;
    setEditingEnderecoId(endereco.id);
    setShowEnderecoForm(true);
    setNovoEndereco({
      apelido: endereco.apelido || '',
      endereco: endereco.endereco || '',
      numero: endereco.numero || '',
      complemento: endereco.complemento || '',
      bairro: endereco.bairro || '',
      cidade: endereco.cidade || '',
      cep: endereco.cep || ''
    });
  };

  const handleCancelarEdicaoEndereco = () => {
    setEditingEnderecoId(null);
    setShowEnderecoForm(false);
    setNovoEndereco({
      apelido: '',
      endereco: '',
      numero: '',
      complemento: '',
      bairro: '',
      cidade: '',
      cep: ''
    });
  };

  const loadTagsProjeto = async () => {
    try {
      setLoadingTags(true);
      
      // Buscar project_id do card
      const { data: projectData } = await supabase
        .from('project_items')
        .select('project_id')
        .eq('id', card.id)
        .single();
      
      if (!projectData?.project_id) {
        console.error('Project ID não encontrado');
        return;
      }
      
      // Buscar configurações do projeto
      const { data: projectSettings } = await supabase
        .from('projects')
        .select('settings')
        .eq('id', projectData.project_id)
        .single();
      
      if (projectSettings?.settings?.gas_distributor?.tags_configuradas) {
        setTagsDisponiveis(projectSettings.settings.gas_distributor.tags_configuradas);
      }
    } catch (error) {
      console.error('Erro ao carregar tags do projeto:', error);
    } finally {
      setLoadingTags(false);
    }
  };

  // Função auxiliar para obter nome da tag pelo ID
  const getTagNameById = (tagId) => {
    const tag = tagsDisponiveis.find(t => t.id === tagId);
    return tag?.nome || tagId;
  };

  // Adicionar tag localmente (sem salvar no banco)
  const handleAdicionarTagLocal = (tagId) => {
    if (tagsCliente.includes(tagId) || tagsPendentes.includes(tagId)) {
      return; // Tag já existe
    }
    
    // Se estava na lista de removidas, tirar de lá
    if (tagsRemovidas.includes(tagId)) {
      setTagsRemovidas(prev => prev.filter(t => t !== tagId));
    }
    
    setTagsPendentes(prev => [...prev, tagId]);
  };

  // Remover tag localmente (sem salvar no banco)
  const handleRemoverTagLocal = (tagId) => {
    // Se estava na lista de pendentes, só tirar de lá
    if (tagsPendentes.includes(tagId)) {
      setTagsPendentes(prev => prev.filter(t => t !== tagId));
      return;
    }
    
    // Se está nas tags do cliente, adicionar às removidas
    if (tagsCliente.includes(tagId)) {
      setTagsRemovidas(prev => [...prev, tagId]);
    }
  };

  // Salvar todas as alterações de tags no banco
  const handleSalvarTags = async () => {
    try {
      // Calcular novas tags: tags atuais + pendentes - removidas
      const novasTags = [
        ...tagsCliente.filter(t => !tagsRemovidas.includes(t)),
        ...tagsPendentes
      ];
      
      const { error } = await supabase
        .from('cliente_distribuidor')
        .update({ tags_desconto: novasTags })
        .eq('id', card.entity_id);

      if (error) throw error;

      // Atualizar estado local
      setTagsCliente(novasTags);
      setCliente(prev => ({ ...prev, tags_desconto: novasTags }));
      setTagsPendentes([]);
      setTagsRemovidas([]);
      
      alert('Tags salvas com sucesso!');
    } catch (error) {
      console.error('Erro ao salvar tags:', error);
      alert('Erro ao salvar tags. Tente novamente.');
    }
  };

  // Verificar se há alterações pendentes
  const temAlteracoesPendentes = tagsPendentes.length > 0 || tagsRemovidas.length > 0;

  // Tags visíveis (atuais + pendentes - removidas)
  const tagsVisiveis = [
    ...tagsCliente.filter(t => !tagsRemovidas.includes(t)),
    ...tagsPendentes
  ];

  const formatPhoneNumber = (phone) => {
    if (!phone) return '';
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length === 13) {
      return `(${cleaned.slice(2, 4)}) ${cleaned.slice(4, 9)}-${cleaned.slice(9)}`;
    }
    if (cleaned.length === 11) {
      return `(${cleaned.slice(0, 2)}) ${cleaned.slice(2, 7)}-${cleaned.slice(7)}`;
    }
    return phone;
  };

  const formatDateTime = (date) => {
    if (!date) return '-';
    const d = new Date(date);
    return d.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (value) => {
    if (!value) return 'R$ 0,00';
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
  };

  // Normaliza URL de avatar (corrige protocolo quebrado e força https)
  const normalizeAvatarUrl = (url) => {
    try {
      if (!url || typeof url !== 'string') return null;
      let out = url.trim();
      if (/^https:\/pps\./i.test(out)) {
        out = out.replace(/^https:\/pps\./i, 'https://pps.');
      }
      if (/^http:\/pps\./i.test(out)) {
        out = out.replace(/^http:\/pps\./i, 'https://pps.');
      }
      if (/^http:\/\//i.test(out)) {
        out = out.replace(/^http:\/\//i, 'https://');
      }
      return out;
    } catch (_) {
      return url || null;
    }
  };

  // Detecta se é URL pública do Supabase Storage
  const isPublicStorageUrl = (url) => {
    try {
      if (!url || typeof url !== 'string') return false;
      return /\/storage\/v1\/object\/public\//.test(url);
    } catch (_) { return false; }
  };

  // Detecta e extrai referência de Storage
  const parseStorageRef = (url) => {
    try {
      if (!url || typeof url !== 'string') return null;
      const raw = String(url).trim();
      if (/whatsapp\.net|fbcdn\.net|cdn\.|googleusercontent\.com|akamaihd\.net/i.test(raw)) return null;
      const m = raw.match(/\/storage\/v1\/object\/(public\/)?([^/]+)\/(.+)$/);
      if (m) {
        return { bucket: m[2], path: m[3] };
      }
      if (raw.includes(':') && !raw.startsWith('http')) {
        const [bucket, ...rest] = raw.split(':');
        const path = rest.join(':').replace(/^\//, '');
        if (bucket && path) return { bucket, path };
      }
      return null;
    } catch (_) {
      return null;
    }
  };

  // Obtém URL assinada do Storage via backend
  const getSignedUrl = async (bucket, path) => {
    try {
      const caminho_storage = `${bucket}:${path}`;
      const response = await fetch(API_ENDPOINTS.MEDIA_SIGNED_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ caminho_storage, expiresIn: 3600 })
      });
      if (!response.ok) return null;
      const data = await response.json();
      return data?.signedUrl || data?.url || null;
    } catch (_) {
      return null;
    }
  };

  // Processa URL do avatar
  const processAvatarUrl = async (url) => {
    if (!url) return null;
    const normalized = normalizeAvatarUrl(url);
    if (!normalized) return null;
    
    if (isPublicStorageUrl(normalized)) {
      return normalized;
    }
    
    const ref = parseStorageRef(normalized);
    if (ref) {
      const signed = await getSignedUrl(ref.bucket, ref.path);
      if (signed) return signed;
    }
    
    if (/^https?:\/\//i.test(normalized)) {
      const sep = normalized.includes('?') ? '&' : '?';
      return `${normalized}${sep}v=${Math.floor(Date.now() / 60000)}`;
    }
    
    return normalized;
  };

  // Efeito para processar URL do avatar quando contato carrega
  useEffect(() => {
    if (contato?.image_url) {
      setAvatarError(false);
      processAvatarUrl(contato.image_url).then(url => {
        setProcessedAvatarUrl(url);
      });
    }
  }, [contato?.image_url]);

  const getStatusClass = (status) => {
    const statusMap = {
      'Confirmado': 'confirmado',
      'Em rota': 'em-rota',
      'Entregue': 'entregue',
      'Cancelado': 'cancelado',
      'Pendente': 'pendente'
    };
    return statusMap[status] || 'outro';
  };

  const renderMessageContent = (msg) => {
    if (msg.tipo_conteudo === 'imagem' && msg.url_midia) {
      return (
        <div className="message-image">
          <img src={msg.url_midia} alt="Imagem enviada" />
          {msg.transcricao && <p className="transcription">{msg.transcricao}</p>}
        </div>
      );
    }
    if (msg.tipo_conteudo === 'audio') {
      return (
        <div className="message-audio">
          <AppIcon name="mic" />
          {msg.transcricao && <p className="transcription">{msg.transcricao}</p>}
        </div>
      );
    }
    return <p className="message-text">{msg.conteudo}</p>;
  };

  if (loading) {
    return (
      <div className="gas-client-modal-overlay" onClick={onClose}>
        <div className="gas-client-modal" onClick={e => e.stopPropagation()}>
          <div className="modal-loading">
            <AppIcon name="progress_activity" className="spinning" />
            <p>Carregando dados do cliente...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="gas-client-modal-overlay" onClick={onClose}>
      <div className="gas-client-modal" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="modal-header">
          <div className="header-avatar">
            {processedAvatarUrl && !avatarError ? (
              <img 
                src={processedAvatarUrl} 
                alt={contato?.nome || 'Cliente'} 
                onError={() => setAvatarError(true)}
                referrerPolicy="no-referrer"
              />
            ) : (
              <AppIcon name="person" />
            )}
          </div>
          <div className="header-info">
            <h2>{contato?.nome || 'Cliente'}</h2>
            <p>{formatPhoneNumber(contato?.numero)}</p>
            <span className="pedido-status-badge">
              <AppIcon name="local_shipping" />
              {ultimoPedido ? ultimoPedido.status : 'Em aberto'}
            </span>
          </div>
          <button
            type="button"
            className="header-action-button"
            onClick={() => setShowConversationModal(true)}
            disabled={!contato?.id || !resolvedConnectionId}
            title={!resolvedConnectionId ? 'Conexao nao identificada para este contato' : 'Abrir atendimento'}
          >
            <AppIcon name="headset_mic" />
            Ver atendimento
          </button>
          <button className="close-button" onClick={onClose}>
            <AppIcon name="close" />
          </button>
        </div>

        {/* Tabs */}
        <div className="modal-tabs">
          <button 
            className={`tab-button ${activeTab === 'dados' ? 'active' : ''}`}
            onClick={() => setActiveTab('dados')}
          >
            <AppIcon name="person" />
            Dados
          </button>
          <button 
            className={`tab-button ${activeTab === 'enderecos' ? 'active' : ''}`}
            onClick={() => setActiveTab('enderecos')}
          >
            <AppIcon name="location_on" />
            Endereços
          </button>
          <button 
            className={`tab-button ${activeTab === 'pedidos' ? 'active' : ''}`}
            onClick={() => setActiveTab('pedidos')}
          >
            <AppIcon name="shopping_bag" />
            Pedidos
          </button>
          <button 
            className={`tab-button ${activeTab === 'conversas' ? 'active' : ''}`}
            onClick={() => setActiveTab('conversas')}
          >
            <AppIcon name="chat" />
            Conversas
          </button>
          <button 
            className={`tab-button ${activeTab === 'tags' ? 'active' : ''}`}
            onClick={() => {
              setActiveTab('tags');
              if (tagsDisponiveis.length === 0) {
                loadTagsProjeto();
              }
            }}
          >
            <AppIcon name="label" />
            Tags
          </button>
        </div>

        {/* Content */}
        <div className="modal-content">
          {/* Aba Dados */}
          {activeTab === 'dados' && (
            <div className="tab-content dados-tab">
              <div className="info-section">
                <h3>Informações Pessoais</h3>
                <div className="info-grid">
                  <div className="info-item">
                    <span className="label">Nome</span>
                    <span className="value">{contato?.nome || '-'}</span>
                  </div>
                  <div className="info-item">
                    <span className="label">Telefone</span>
                    <span className="value">{formatPhoneNumber(contato?.numero)}</span>
                  </div>
                  <div className="info-item">
                    <span className="label">Tipo de Cliente</span>
                    <span className="value">{cliente?.tipo_cliente || '-'}</span>
                  </div>
                  <div className="info-item">
                    <span className="label">Status</span>
                    <span className={`value status ${cliente?.status}`}>{cliente?.status || '-'}</span>
                  </div>
                </div>
              </div>

              <div className="info-section">
                <h3>Preferências</h3>
                <div className="info-grid">
                  <div className="info-item">
                    <span className="label">Forma de Pagamento</span>
                    <span className="value">{cliente?.forma_pagamento_preferida || '-'}</span>
                  </div>
                  <div className="info-item">
                    <span className="label">Horário Preferido</span>
                    <span className="value">{cliente?.horario_preferido_entrega || '-'}</span>
                  </div>
                </div>
              </div>

              {cliente?.tags_desconto?.length > 0 && (
                <div className="info-section">
                  <h3>Tags de Desconto</h3>
                  <div className="tags-container">
                    {cliente.tags_desconto.map((tagId, i) => (
                      <span key={i} className="tag-badge">{getTagNameById(tagId)}</span>
                    ))}
                  </div>
                </div>
              )}

              {cliente?.observacoes && (
                <div className="info-section">
                  <h3>Observações</h3>
                  <p className="observacoes-text">{cliente.observacoes}</p>
                </div>
              )}
            </div>
          )}

          {/* Aba Endereços */}
          {activeTab === 'enderecos' && (
            <div className="tab-content enderecos-tab">
              <div className="enderecos-tab-actions">
                <button
                  type="button"
                  className="btn-add-endereco"
                  onClick={() => {
                    if (showEnderecoForm) {
                      handleCancelarEdicaoEndereco();
                      return;
                    }
                    setShowEnderecoForm(true);
                  }}
                >
                  <AppIcon name={showEnderecoForm ? 'close' : 'add_location_alt'} />
                  {showEnderecoForm
                    ? (editingEnderecoId ? 'Cancelar edição' : 'Fechar formulário')
                    : 'Adicionar endereço'}
                </button>
              </div>

              {showEnderecoForm && (
                <form className="novo-endereco-form" onSubmit={handleSalvarNovoEndereco}>
                  <div className="ne-grid">
                    <label className="ne-field ne-span-2">
                      Apelido (opcional)
                      <input
                        value={novoEndereco.apelido}
                        onChange={(e) => setNovoEndereco((p) => ({ ...p, apelido: e.target.value }))}
                        placeholder="Ex.: Casa, Trabalho"
                      />
                    </label>
                    <label className="ne-field ne-span-2">
                      Logradouro *
                      <input
                        required
                        value={novoEndereco.endereco}
                        onChange={(e) => setNovoEndereco((p) => ({ ...p, endereco: e.target.value }))}
                        placeholder="Rua / Avenida"
                      />
                    </label>
                    <label className="ne-field">
                      Número
                      <input
                        value={novoEndereco.numero}
                        onChange={(e) => setNovoEndereco((p) => ({ ...p, numero: e.target.value }))}
                      />
                    </label>
                    <label className="ne-field">
                      Complemento
                      <input
                        value={novoEndereco.complemento}
                        onChange={(e) => setNovoEndereco((p) => ({ ...p, complemento: e.target.value }))}
                      />
                    </label>
                    <label className="ne-field">
                      Bairro
                      <input
                        value={novoEndereco.bairro}
                        onChange={(e) => setNovoEndereco((p) => ({ ...p, bairro: e.target.value }))}
                      />
                    </label>
                    <label className="ne-field">
                      Cidade
                      <input
                        value={novoEndereco.cidade}
                        onChange={(e) => setNovoEndereco((p) => ({ ...p, cidade: e.target.value }))}
                      />
                    </label>
                    <label className="ne-field">
                      CEP
                      <input
                        value={novoEndereco.cep}
                        onChange={(e) => setNovoEndereco((p) => ({ ...p, cep: e.target.value }))}
                      />
                    </label>
                  </div>
                  <div className="ne-actions">
                    <button type="submit" className="ne-save" disabled={savingEndereco}>
                      {savingEndereco
                        ? 'Salvando...'
                        : (editingEnderecoId ? 'Salvar alterações' : 'Salvar endereço')}
                    </button>
                  </div>
                </form>
              )}

              {enderecos.length === 0 && !showEnderecoForm ? (
                <div className="empty-state">
                  <AppIcon name="location_off" />
                  <p>Nenhum endereço cadastrado</p>
                  <p className="empty-hint">Use &quot;Adicionar endereço&quot; para cadastrar manualmente.</p>
                </div>
              ) : enderecos.length > 0 ? (
                <div className="enderecos-list">
                  {enderecos.map((end, i) => (
                    <div key={end.id || i} className={`endereco-card ${end.eh_principal ? 'principal' : ''}`}>
                      <div className="endereco-header">
                        <span className="endereco-apelido">{end.apelido || `Endereço ${i + 1}`}</span>
                        <div className="endereco-header-actions">
                          {end.eh_principal && <span className="principal-badge">Principal</span>}
                          <button
                            type="button"
                            className="btn-editar-endereco"
                            onClick={() => handleIniciarEdicaoEndereco(end)}
                          >
                            <AppIcon name="edit" />
                            Editar
                          </button>
                        </div>
                      </div>
                      <div className="endereco-detalhes">
                        <p className="endereco-rua">
                          {end.endereco}{end.numero ? `, ${end.numero}` : ''}
                          {end.complemento && ` - ${end.complemento}`}
                        </p>
                        <p className="endereco-bairro">
                          {end.bairro} - {end.cidade}
                        </p>
                        <p className="endereco-cep">CEP: {end.cep}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : null}
            </div>
          )}

          {/* Aba Pedidos */}
          {activeTab === 'pedidos' && (
            <div className="tab-content pedidos-tab">
              <div className="pedidos-tab-actions">
                <button
                  type="button"
                  className="btn-novo-pedido"
                  onClick={() => {
                    if (!effectiveProjectId) {
                      alert('Projeto não identificado. Recarregue o quadro e tente de novo.');
                      return;
                    }
                    setShowNovoPedido(true);
                  }}
                >
                  <AppIcon name="add_shopping_cart" />
                  Novo pedido
                </button>
              </div>

              {pedidos.length === 0 ? (
                <div className="empty-state">
                  <AppIcon name="shopping_bag" />
                  <p>Nenhum pedido realizado</p>
                  <p className="empty-hint">Use &quot;Novo pedido&quot; para registrar manualmente.</p>
                </div>
              ) : (
                <div className="pedidos-list">
                  {pedidos.map((pedido) => (
                    <div 
                      key={pedido.id} 
                      className="pedido-card clickable"
                      onClick={() => setSelectedPedidoId(pedido.id)}
                    >
                      <div className="pedido-header">
                        <span className="pedido-id">#{pedido.id.slice(0, 8)}</span>
                        <span className={`pedido-status ${getStatusClass(pedido.status)}`}>
                          {pedido.status}
                        </span>
                      </div>
                      <div className="pedido-info">
                        <div className="pedido-info-item">
                          <span className="label">Itens</span>
                          <span className="value">
                            {pedido.produtos 
                              ? (Array.isArray(pedido.produtos) 
                                  ? pedido.produtos.reduce((sum, p) => sum + (p.quantidade || 0), 0)
                                  : pedido.quantidade || 0)
                              : pedido.quantidade || 0}
                          </span>
                        </div>
                        <div className="pedido-info-item">
                          <span className="label">Total</span>
                          <span className="value">{formatCurrency(pedido.preco_total)}</span>
                        </div>
                        <div className="pedido-info-item">
                          <span className="label">Forma Pgto</span>
                          <span className="value">{pedido.forma_pagamento || '-'}</span>
                        </div>
                        <div className="pedido-info-item">
                          <span className="label">Data</span>
                          <span className="value">{formatDateTime(pedido.created_at)}</span>
                        </div>
                      </div>
                      {pedido.endereco_entrega && (
                        <div className="pedido-endereco">
                          <AppIcon name="location_on" />
                          <span>{pedido.endereco_entrega}</span>
                        </div>
                      )}
                      <div className="pedido-click-hint">
                        <AppIcon name="visibility" />
                        Clique para ver detalhes
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Aba Conversas */}
          {activeTab === 'conversas' && (
            <div className="tab-content conversas-tab">
              {conversas.length > 1 && (
                <div className="conversas-selector">
                  <select 
                    value={selectedConversation?.id || ''} 
                    onChange={(e) => {
                      const conv = conversas.find(c => c.id === e.target.value);
                      setSelectedConversation(conv);
                      loadMessages(e.target.value);
                    }}
                  >
                    {conversas.map(conv => (
                      <option key={conv.id} value={conv.id}>
                        {formatDateTime(conv.criado_em)}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              
              <div className="messages-container">
                {messages.length === 0 ? (
                  <div className="empty-state">
                    <AppIcon name="chat_bubble" />
                    <p>Nenhuma mensagem encontrada</p>
                  </div>
                ) : (
                  messages.map((msg) => (
                    <div 
                      key={msg.id} 
                      className={`message ${msg.from_me || msg.direcao === 'saida' ? 'outgoing' : 'incoming'}`}
                    >
                      <div className="message-content">
                        {renderMessageContent(msg)}
                        <span className="message-time">{formatDateTime(msg.criado_em)}</span>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {/* Aba Tags */}
          {activeTab === 'tags' && (
            <div className="tab-content tags-tab">
              {loadingTags ? (
                <div className="loading-state">
                  <AppIcon name="progress_activity" className="spinning" />
                  <p>Carregando tags...</p>
                </div>
              ) : (
                <>
                  {/* Tags do Cliente */}
                  <div className="info-section">
                    <h3>Tags do Cliente</h3>
                    {tagsVisiveis.length === 0 ? (
                      <p className="empty-message">Nenhuma tag aplicada a este cliente</p>
                    ) : (
                      <div className="tags-cliente-list">
                        {tagsVisiveis.map((tagId, i) => {
                          const isPendente = tagsPendentes.includes(tagId);
                          return (
                            <div key={i} className={`tag-item cliente-tag ${isPendente ? 'pendente' : ''}`}>
                              <AppIcon name="label" />
                              <span className="tag-name">{getTagNameById(tagId)}</span>
                              {isPendente && <span className="pendente-badge">(nova)</span>}
                              <button 
                                className="remove-tag-btn"
                                onClick={() => handleRemoverTagLocal(tagId)}
                                title="Remover tag"
                              >
                                <AppIcon name="close" />
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {/* Tags Disponíveis */}
                  <div className="info-section">
                    <h3>Tags Disponíveis no Projeto</h3>
                    {tagsDisponiveis.length === 0 ? (
                      <div className="empty-state">
                        <AppIcon name="label_off" />
                        <p>Nenhuma tag configurada neste projeto</p>
                        <small>Configure tags nas configurações do projeto</small>
                      </div>
                    ) : (
                      <>
                        <div className="tags-disponiveis-list">
                          {tagsDisponiveis.map((tag, i) => {
                            const jaAplicada = tagsVisiveis.includes(tag.id);
                            return (
                              <div 
                                key={i} 
                                className={`tag-item disponivel-tag ${jaAplicada ? 'aplicada' : ''}`}
                              >
                                <div className="tag-info">
                                  <AppIcon name={jaAplicada ? 'check_circle' : 'label'} />
                                  <div className="tag-details">
                                    <span className="tag-name">{tag.nome}</span>
                                    {tag.desconto_percentual > 0 && (
                                      <span className="tag-desconto">
                                        {tag.desconto_percentual}% de desconto
                                      </span>
                                    )}
                                  </div>
                                </div>
                                {!jaAplicada && (
                                  <button 
                                    className="add-tag-btn"
                                    onClick={() => handleAdicionarTagLocal(tag.id)}
                                    title="Adicionar tag ao cliente"
                                  >
                                    <AppIcon name="add" />
                                  </button>
                                )}
                              </div>
                            );
                          })}
                        </div>
                        
                        {/* Botão de Salvar */}
                        {temAlteracoesPendentes && (
                          <div className="save-tags-section">
                            <button 
                              className="save-tags-btn"
                              onClick={handleSalvarTags}
                            >
                              <AppIcon name="save" />
                              Salvar Alterações
                            </button>
                            <span className="pending-count">
                              {tagsPendentes.length > 0 && `${tagsPendentes.length} para adicionar`}
                              {tagsPendentes.length > 0 && tagsRemovidas.length > 0 && ' | '}
                              {tagsRemovidas.length > 0 && `${tagsRemovidas.length} para remover`}
                            </span>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Modal de Detalhes do Pedido */}
      {selectedPedidoId && (
        <GasDistributorPedidoModal
          pedidoId={selectedPedidoId}
          onClose={() => setSelectedPedidoId(null)}
        />
      )}

      {showNovoPedido && effectiveProjectId && (
        <GasDistributorNovoPedidoModal
          open={showNovoPedido}
          onClose={() => setShowNovoPedido(false)}
          clienteId={card.entity_id}
          projectId={effectiveProjectId}
          enderecos={enderecos}
          pedidosExistentes={pedidos}
          onSuccess={(result) => {
            if (result?.pedido_id) {
              setSelectedPedidoId(result.pedido_id);
            }
            loadClientData();
          }}
        />
      )}

      <CRMConversationModal
        isOpen={showConversationModal}
        onClose={() => setShowConversationModal(false)}
        contact={contato}
        connectionId={resolvedConnectionId}
        companyId={companyId || card?.company_id}
        card={card}
      />
    </div>
  );
}
